import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { useWindowResize, useWindowScroll } from '../../../hooks';

import { PopoverPosition, Position } from './Popover.types';

const OFFSET = 16;

export const usePopoverPosition = (
    triggerRef: RefObject<HTMLElement | null>,
    popoverRef: RefObject<HTMLElement | null>,
    isOpen: boolean,
    positionType: PopoverPosition,
): Position => {
    const [coordinates, setCoordinates] = useState<Position>({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) {
            return;
        }

        const rect = triggerRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current?.getBoundingClientRect();
        const popoverHeight = popoverRect?.height || 0;
        const popoverWidth = popoverRect?.width || 0;

        let top = 0;
        let left = 0;

        switch (positionType) {
            case 'top':
                top = rect.top - popoverHeight - OFFSET;
                left = rect.left + rect.width / 2 - popoverWidth / 2;
                break;
            case 'top-left':
                top = rect.top - popoverHeight - OFFSET;
                left = rect.left + rect.width - popoverWidth + 8;
                break;
            case 'top-right':
                top = rect.top - popoverHeight - OFFSET;
                left = rect.left - 8;
                break;
            case 'bottom':
                top = rect.bottom + OFFSET;
                left = rect.left + rect.width / 2 - popoverWidth / 2;
                break;
            case 'bottom-left':
                top = rect.bottom + OFFSET;
                left = rect.left + rect.width - popoverWidth + 8;
                break;
            case 'bottom-right':
                top = rect.bottom + OFFSET;
                left = rect.left - 8;
                break;
        }

        setCoordinates({ top, left });
    }, [positionType, triggerRef, popoverRef]);

    useWindowResize(updatePosition, 100);
    useWindowScroll(updatePosition, 0);

    useLayoutEffect(() => {
        if (isOpen && popoverRef.current) {
            updatePosition();
        }
    }, [isOpen, popoverRef, updatePosition]);

    useLayoutEffect(() => {
        if (!isOpen || !popoverRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            updatePosition();
        });

        resizeObserver.observe(popoverRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isOpen, popoverRef, updatePosition]);

    return coordinates;
};

export const useAutoClose = (isOpen: boolean, onClose: () => void, delay: number = 2000, autoClose: boolean = true) => {
    useEffect(() => {
        if (!isOpen || !autoClose) {
            return;
        }

        const timerId = setTimeout(() => {
            onClose();
        }, delay);

        return () => {
            clearTimeout(timerId);
        };
    }, [isOpen, onClose, delay, autoClose]);
};
